import { useEffect, useId, useRef, useState } from "react";
import { Input } from "./Field";
import { IconCalendar } from "./icons";
import {
  formatBirthDateDisplay,
  formatBirthDigits,
  isoToBirthDisplay,
  parseBirthDateToIso,
} from "../../utils/birthDateMask";
import u from "./ui.module.css";

/**
 * Дата рождения: ввод строкой ДД.ММ.ГГГГ (+ опционально календарь).
 * value / onChange — формат YYYY-MM-DD (как у type="date").
 */
export default function BirthDateInput({
  value,
  onChange,
  onBlur,
  placeholder = "ДД.ММ.ГГГГ",
  max,
  min,
  allowPicker = true,
  className = "",
  ...rest
}) {
  const pickerId = useId();
  const pickerRef = useRef(null);
  const [text, setText] = useState(() => formatBirthDateDisplay(value));

  useEffect(() => {
    const next = formatBirthDateDisplay(value);
    setText((prev) => {
      const prevIso = parseBirthDateToIso(prev);
      if (prevIso && prevIso === String(value || "").slice(0, 10)) return prev;
      if (!value) return prev && !parseBirthDateToIso(prev) ? prev : "";
      return next || isoToBirthDisplay(value) || "";
    });
  }, [value]);

  const emit = (iso) => {
    onChange?.(iso || "");
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (!raw.replace(/\D/g, "")) {
      setText("");
      emit("");
      return;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
      const iso = parseBirthDateToIso(raw.trim());
      if (iso) {
        setText(isoToBirthDisplay(iso));
        emit(iso);
        return;
      }
    }
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const formatted = formatBirthDigits(digits);
    setText(formatted);
    if (digits.length === 8) {
      const iso = parseBirthDateToIso(formatted);
      emit(iso || "");
    } else if (value) {
      emit("");
    }
  };

  const handleBlur = (e) => {
    const iso = parseBirthDateToIso(text);
    if (iso) {
      setText(isoToBirthDisplay(iso));
      if (iso !== value) emit(iso);
    } else if (text.trim()) {
      setText(value ? isoToBirthDisplay(value) : "");
      if (!value) emit("");
    }
    onBlur?.(e);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else el.click();
    } catch {
      el.click();
    }
  };

  return (
    <div className={`${u.birthDateRow} ${className}`.trim()}>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        className={u.birthDateInput}
        {...rest}
      />
      {allowPicker ? (
        <>
          <input
            ref={pickerRef}
            id={pickerId}
            type="date"
            className={u.birthDateNative}
            tabIndex={-1}
            aria-hidden
            value={value || ""}
            max={max}
            min={min}
            onChange={(e) => {
              const iso = e.target.value;
              setText(iso ? isoToBirthDisplay(iso) : "");
              emit(iso);
            }}
          />
          <button
            type="button"
            className={u.birthDatePickerBtn}
            onClick={openPicker}
            tabIndex={-1}
            aria-label="Открыть календарь"
            title="Календарь"
          >
            <IconCalendar width={18} height={18} />
          </button>
        </>
      ) : null}
    </div>
  );
}
